import { getItemHref } from "@/app/navigation/item"
import { PriceWrapper } from "@/app/price"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { NftSearchItem } from "@anaconda/core/nft/search/types"
import type { NftId } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import { Card, Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import { NftsToBuyMoleculeReatom } from "../buy/nfts-to-buy/molecule.reatom"
import { ListingSourceBadge } from "../listing-source-badge"
import { NftSelectionToggle } from "../nft-selection/nft-selection-toggle"
import Rarity from "../rarity/rarity"
import { Action } from "./action"
import { NftPreviewImage } from "./image"

export const NftPreview = reatomComponent((nft: NftSearchItem) => {
  const { id, image, name, price, lastSellPrice } = nft
  const { selectedNftsToBuy } = useMolecule(NftsToBuyMoleculeReatom)
  const selectedNfts = selectedNftsToBuy()
  const selected = selectedNfts.some(({ id: nftId }) => nftId === id)
  const selectionAvailable = true // Always available in buy context

  return (
    <NftPreviewLayout
      selected={selected}
      nftId={id}
      image={
        <div className="relative">
          {selectionAvailable && (
            <NftSelectionToggle
              nft={nft}
              className={cn(
                "invisible absolute top-2 right-2 z-30",
                "group-hover:visible",
                selected && "visible",
              )}
            />
          )}
          <Link href={getItemHref(id)} prefetch={false}>
            <NftPreviewImage key={image} nftName={name} imageSrc={image} />
          </Link>
          <div className="absolute bottom-3 z-30 flex w-full items-center justify-center">
            <div className="transition-opacity duration-200 group-hover:opacity-100 md:opacity-0">
              <Action nftPreview={nft} />
            </div>
          </div>
        </div>
      }
      content={
        <Link
          className="flex flex-col gap-1 p-1"
          href={getItemHref(id)}
          prefetch={false}
        >
          <div className="flex items-center justify-between gap-1">
            <span className="overflow-hidden overflow-ellipsis text-nowrap">
              {name}
            </span>
            {nft.rarity && <Rarity rarity={nft.rarity} />}
          </div>
          <span className="text-lg">
            {price ? (
              <span className="inline-flex items-center gap-2">
                <PriceWrapper {...price} />
                <ListingSourceBadge
                  listingSource={nft.listingSource}
                  platform={nft.bestSellOrder?.platform}
                />
              </span>
            ) : (
              <span className="text-foreground/60">
                <Trans>Not listed</Trans>
              </span>
            )}
          </span>
          {/* A Solana last sale counts sales through Rarible only, which
              hardly any NFT has had yet: the line read "—" on nearly every
              card (1 in ~530 sampled on 2026-10-16). */}
          {nft.collection.blockchain !== Blockchain.SOLANA && (
            <span className="text-2xs text-foreground/60">
              <Trans>Last: </Trans>
              {lastSellPrice ? <PriceWrapper {...lastSellPrice} /> : "—"}
            </span>
          )}
        </Link>
      }
    />
  )
}, "NftPreview")

export function NftPreviewSkeleton() {
  return (
    <NftPreviewLayout
      image={<Skeleton className="aspect-square w-full rounded-md" />}
      content={
        <div className="flex flex-col gap-1 p-1">
          <Skeleton className="h-6 w-[25%]" />
          <Skeleton className="h-7 w-[30%]" />
          <Skeleton className="h-4 w-[50%]" />
        </div>
      }
    />
  )
}

interface LayoutProps {
  nftId?: NftId
  image: React.ReactNode
  content: React.ReactNode
  selected?: boolean
}

function NftPreviewLayout({ nftId, image, content, selected }: LayoutProps) {
  return (
    <Card
      className={cn(
        "group flex flex-col gap-3 rounded-xl p-2",
        "transition-all duration-200 will-change-transform [backface-visibility:hidden] [transform-style:preserve-3d] hover:scale-105 hover:border-foreground/20 hover:bg-foreground/5",
        !!selected && "[&&]:border-brand",
      )}
      data-testid="nft-preview"
      data-nft-id={nftId}
    >
      {image}
      {content}
    </Card>
  )
}
